import React, { useEffect } from "react";
import { ApiCustomer } from "src/api/api_customer";
import { GetSingleDeviceResponse } from "src/api/generated/frontend_customer_v1";
import { UUID } from "src/api/api_common";

/** Data provided by the {@link DEVICE_CONTEXT} */
export type DeviceContext = {
    /** The currently active context */
    device: GetSingleDeviceResponse;

    /** Reload the devices' information */
    reset: () => void;
};

/** {@link React.Context} to access {@link GetSingleDeviceResponse} */
const DEVICE_CONTEXT = React.createContext<DeviceContext>({
    device: {
        created_at: "",
        dnsname: "",
        expired: false,
        hostname: "",
        online: false,
        uuid: "",
        inbound_access_rules: { groups: [], tags: [], subnets: [] },
        outbound_access_rules: { groups: [], tags: [], subnets: [] },
        subnets: [],
        extra_records: [],
    },

    /**
     * Reset the context
     */
    reset: () => {},
});
DEVICE_CONTEXT.displayName = "DeviceContext";
export default DEVICE_CONTEXT;

/**
 * The properties for {@link DeviceProvider}
 */
export type DeviceProviderProps = {
    /** The uuid to load */
    uuid: UUID;

    /**
     * Children of the provider
     */
    children: React.ReactNode;
};

/**
 * The provider of a device context
 */
export function DeviceProvider(props: DeviceProviderProps) {
    const [device, setDevice] = React.useState<GetSingleDeviceResponse | "loading">("loading");
    let fetching = false;

    /**
     * Fetch the device
     */
    const fetchDevice = async () => {
        if (fetching) return;
        fetching = true;

        const res = await ApiCustomer.devices.getSingle(props.uuid);
        setDevice(res);

        fetching = false;
    };

    useEffect(() => {
        fetchDevice().then();
    }, [props.uuid]);

    if (device === "loading") {
        return <div>Loading ..</div>;
    }

    return (
        <DEVICE_CONTEXT.Provider
            value={{
                device: device,
                reset: fetchDevice,
            }}
        >
            {props.children}
        </DEVICE_CONTEXT.Provider>
    );
}
